/// <amd-module name="Environments/ProviderCreateDetails/Kubernetes/ActionsCreators/KubernetesResourceActionsCreator" />
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import { ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { KubernetesResourceActions } from "Environments/ProviderCreateDetails/Kubernetes/Actions/KubernetesResourceActions";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { AadAuthorizer } from "Environments/EnvironmentsCommon/AadAuthorizer";

import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import { PageContextHelper } from "Environments/Client/PageContextHelper";
import { 
    EnvironmentResource, 
    KubernetesResourcePatchParameters, 
    KubernetesResourceCreateParametersExistingEndpoint, 
    KubernetesResource 
} from "DistributedTask/Client/Contracts/TaskAgent";
import { IVssPageContext } from "VSS/Platform/Context";
import { IAzureKubernetesProviderData, IKubernetesProviderData, IKubernetesCustomProviderData } from "Environments/ProviderCreateDetails/Kubernetes/Types";
import { PageKey, KubernetesProviderType, safeParseJson, setAuthInEnpointRequest, KubernetesEndpointAuthorizationType, Action } from "Environments/ProviderCreateDetails/Kubernetes/Utilities";
import { getErrorMessage, IResourceCreatedResponse } from "Environments/EnvironmentsCommon/Utils";
import * as Resources from "Environments/ProviderCreateDetails/Kubernetes/Resources";

import { ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { publishTelemetry, FeatureNames } from "Environments/ProviderCreateDetails/Kubernetes/Telemetry";
import { IProjectData } from "Tfs/Platform/Page";
import { AccessTokenActions } from "Environments/ProviderCreateDetails/Kubernetes/Actions/AccessTokenActions";

const pollTimeLimit = 2 * 60 * 1000;

export class KubernetesResourceActionsCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "kubernetes-resource-actions-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);

        this._actions = ActionsHubManager.CreateActionsHub<KubernetesResourceActions>(KubernetesResourceActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions>(MessagesActions, instanceId);
        this._accessTokenActions = ActionsHubManager.GetActionsHub<AccessTokenActions>(AccessTokenActions, instanceId);
    }

    public createResource(
        pageContext: IVssPageContext, 
        resourceData: IKubernetesProviderData, 
        environmentId: number, 
        resourceCreatedResponse: IResourceCreatedResponse, 
        tenantId: string | undefined, 
        accessTokens: { [key: string]: string }, 
        isSubscriptionCreated?: boolean) {

        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        let serviceEndpointObject: ServiceEndpoint;

        const addKubernetesResource = (endpoint: ServiceEndpoint) => {
            this._addKubernetesResource(
                pageContext,
                resourceData.kubernetesNamespace,
                resourceData.kubernetesNamespace,
                resourceData.clusterName,
                endpoint.id,
                environmentId,
                resourceCreatedResponse);
        }
        
        this._actions.updateCreationState.invoke("InProgress");

        if (resourceData.type === KubernetesProviderType.AzureKubernetesServiceProvider) {
            // resourceData.subscription exists
            serviceEndpointObject = this._getAzureKubernetesEndpointObject(resourceData as IAzureKubernetesProviderData)
            const kubernetesNamespace =  resourceData.kubernetesNamespace || "";
            // Need to remove after 1-2 sprint
            publishTelemetry(pageContext, FeatureNames.AddK8sResProviderType, { "KubernetesProviderType": KubernetesProviderType.AzureKubernetesServiceProvider, "EnvironmentId": environmentId.toString(), "kubernetesNamespace": kubernetesNamespace} );
            publishTelemetry(pageContext, FeatureNames.AddK8sResWithExistingSubscription, { "KubernetesProviderType": KubernetesProviderType.AzureKubernetesServiceProvider, "EnvironmentId": environmentId.toString(), "kubernetesNamespace": kubernetesNamespace, "NewSubscriptionCreated": isSubscriptionCreated ? "true" : "false"} );
            publishTelemetry(pageContext, FeatureNames.EnvironmentCreationWorkFlow, { "Action": Action.AddAKSResProvider, "EnvironmentId": environmentId.toString(), "NewSubscriptionCreated": isSubscriptionCreated ? "true" : "false", "IskubernetesNamespacePresent": kubernetesNamespace ? "True" : "False"} );
            // Create an endpoint and then poll to figure out completion state
            this._createEndpoint(
                pageContext,
                serviceEndpointObject,
                (createdEndpoint: ServiceEndpoint) => {
                    const onEndpointCreationFailure = (error: any) => {
                        publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { endpointCreationFailed: "true", provider: serviceEndpointObject.data.authorizationType });
                        this._actions.updateCreationState.invoke("Failed");
                        resourceCreatedResponse.onError(getErrorMessage(error));

                        environmentService.deleteServiceEndpoint(createdEndpoint.id)
                            .then(() => { },
                                (error) => {
                                    // Retry once more
                                    environmentService.deleteServiceEndpoint(createdEndpoint.id).
                                        then(() => { }, () => { });
                                });
                    }

                    this._pollForEndpointCompletionWithTimeout(pageContext, createdEndpoint, addKubernetesResource, onEndpointCreationFailure, pollTimeLimit);
                },
                resourceCreatedResponse);
        }
        else {

            serviceEndpointObject = this._getKubernetesServiceAccountEndpointObject(resourceData as IKubernetesCustomProviderData);
            // Need to remove after 1-2 sprint
            publishTelemetry(pageContext, FeatureNames.AddK8sResProviderType, { "KubernetesProviderType": KubernetesProviderType.CustomProvider, "EnvironmentId": environmentId.toString(), "kubernetesNamespace": serviceEndpointObject.name} );
            publishTelemetry(pageContext, FeatureNames.EnvironmentCreationWorkFlow, { "Action": Action.AddCustomProvider, "EnvironmentId": environmentId.toString(), "IskubernetesNamespacePresent": serviceEndpointObject.name ? "True" : "False" });

            if (this._isEnvironmentOwnedServiceConnectionsFeatureFlagEnabled(pageContext)) {
                // FF "Environments.EnableEnvironmentOwnedServiceConnections" has never been enabled anywhere
                // this._addKubernetesResource2(
                //     pageContext,
                //     resourceData.kubernetesNamespace,
                //     resourceData.kubernetesNamespace,
                //     resourceData.clusterName,
                //     serviceEndpointObject,
                //     environmentId,
                //     resourceCreatedResponse);
            }
            else {
                this._createEndpoint(pageContext, serviceEndpointObject, addKubernetesResource, resourceCreatedResponse);
            }
        }
    }

    public validateGenericEndpoint(pageContext: IVssPageContext, resourceData: IKubernetesCustomProviderData): void {
        const serviceEndpointObject = this._getKubernetesServiceAccountEndpointObject(resourceData as IKubernetesCustomProviderData);

        this._actions.updateCreationState.invoke("InProgress");
        const testConnectionServiceEndpointRequest: ServiceEndpointRequest = {
            serviceEndpointDetails: serviceEndpointObject,
            dataSourceDetails: {
                dataSourceName: "TestKubernetesNamespaceConnection",
                dataSourceUrl: "",
                headers: [],
                requestContent: "",
                requestVerb: "",
                resourceUrl: "",
                parameters: { KubernetesNamespace: resourceData.kubernetesNamespace! },
                resultSelector: "",
                initialContextTemplate: ""
            },
            resultTransformationDetails: {
                resultTemplate: "",
                callbackContextTemplate: "",
                callbackRequiredTemplate: ""
            }
        }

        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        environmentService.executeServiceEndpointRequestResult(testConnectionServiceEndpointRequest, "0")
            .then((result: ServiceEndpointRequestResult) => {
                if (result) {
                    if (!!result.errorMessage) {
                        this._actions.genericValidationResult.invoke({
                            type: "Warn",
                            message: result.errorMessage
                        });

                        return;
                    }

                    if (result.result && result.result.length) {
                        let resultObject = undefined;
                        try {
                            resultObject = JSON.parse(result.result[0]);
                        }
                        catch{
                        }

                        if (resultObject && resultObject.metadata && resultObject.metadata.name) {
                            this._actions.genericValidationResult.invoke({ type: "Success", message: "" });
                        }
                        else {
                            this._actions.genericValidationResult.invoke({ type: "Warn", message: result.result });
                        }
                    }
                }
            },
                (error: Error) => {
                    this._actions.genericValidationResult.invoke({ type: "Warn", message: error.message });
                });
    }

    public UpdateResourceAuth(
        pageContext: IVssPageContext, 
        resourceId: number, 
        environmentId: number, 
        resourceData: IKubernetesProviderData, 
        endpoint: ServiceEndpoint, 
        resourceCreatedResponse: IResourceCreatedResponse, 
        tenantId: string | undefined, 
        accessTokens: { [key: string]: string }) {
        
        let serviceEndpointObject: ServiceEndpoint;

        const onUpdate = (endpoint: ServiceEndpoint) => {
            publishTelemetry(pageContext, FeatureNames.ValidateK8sResComplete);
            this._actions.updateCreationState.invoke("Succeeded");
            resourceCreatedResponse.onSuccess(undefined);
        }

        this._actions.updateCreationState.invoke("InProgress");

        if (endpoint.data.authorizationType === KubernetesEndpointAuthorizationType.AzureSubscription) {
            endpoint.data["operation.type"] = "update";

            endpoint.authorization.parameters = {
                "azureEnvironment": "AzureCloud",
                "azureTenantId": endpoint.authorization.parameters["azureTenantId"]
            };

            // Update the endpoint and then poll to figure out completion state
            this._validateEndpointWithAuthCheck(
                pageContext,
                endpoint,
                (updatedEndpoint: ServiceEndpoint) => {
                    const onEndpointCreationFailure = (error: any) => {
                        publishTelemetry(pageContext, FeatureNames.ValidateK8sResFailed, { endpointCreationFailed: "true", provider: serviceEndpointObject.data.authorizationType });
                        this._actions.updateCreationState.invoke("Failed");
                        resourceCreatedResponse.onError(getErrorMessage(error));
                    }

                    this._pollForEndpointCompletionWithTimeout(pageContext, updatedEndpoint, onUpdate, onEndpointCreationFailure, pollTimeLimit);
                },
                resourceCreatedResponse,
                tenantId,
                accessTokens);
        }
        else {
            
            const kubernetesCustomProviderData = resourceData as IKubernetesCustomProviderData;            
            let kubectlSecretJson = safeParseJson(kubernetesCustomProviderData.kubectlSecret);
            
            if (this._isEnvironmentOwnedServiceConnectionsFeatureFlagEnabled(pageContext)) {

                let patchRequest = { 
                    authorizationParameters: {
                        "apiToken": kubectlSecretJson.data["token"],
                        "serviceAccountCertificate": kubectlSecretJson.data["ca.crt"], // this should be safe as we are already doing JSON validations in the UI
                        "acceptUntrustedCerts": kubernetesCustomProviderData.acceptUntrustedCertificates ? "true" : "false"
                    },
                    providerType: KubernetesProviderType.CustomProvider,
                    resourceId: resourceId
                } as KubernetesResourcePatchParameters;
    
                const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
                environmentService.updateKubernetesResource(patchRequest, environmentId)
                    .then((resource: KubernetesResource) => {
                        
                        publishTelemetry(pageContext, FeatureNames.ValidateK8sResComplete);
                        
                        this._actions.updateCreationState.invoke("Succeeded");
                        this._actions.addKubernetesResource.invoke(resource);
                        
                        resourceCreatedResponse.onSuccess(undefined);
    
                    },  (error: any) => {
                        
                        // TODO: check what telemetry needs to published here
                        // publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { endpointCreationFailed: "true", provider: endpoint.data.authorizationType });
    
                        this._actions.updateCreationState.invoke("Failed");
                        resourceCreatedResponse.onError(getErrorMessage(error));
                    }
                );
            }
            else {

                const clonedEndpoint = { ...endpoint };
                clonedEndpoint.authorization = {
                    ...endpoint.authorization,
                    parameters: {
                        ...endpoint.authorization.parameters,
                        "apiToken": kubectlSecretJson.data["token"], // this should be safe as we are already doing JSON validations in the UI
                        "serviceAccountCertificate": kubectlSecretJson.data["ca.crt"],
                    }
                };

                clonedEndpoint.data.acceptUntrustedCerts = kubernetesCustomProviderData.acceptUntrustedCertificates ? "true" : "false";
                this._validateEndpoint(pageContext, clonedEndpoint, onUpdate, resourceCreatedResponse);
            }
        }
    }

    private _createEndpoint(pageContext: IVssPageContext, endpoint: ServiceEndpoint, onCreate: (endpoint: ServiceEndpoint) => void, resourceCreatedResponse: IResourceCreatedResponse) {
        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        environmentService.createServiceEndpoint(endpoint)
            .then((createdEndpoint: ServiceEndpoint) => {
                return onCreate(createdEndpoint);
            }, (error: any) => {
                publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { endpointCreationFailed: "true", provider: endpoint.data.authorizationType });
                this._actions.updateCreationState.invoke("Failed");
                resourceCreatedResponse.onError(getErrorMessage(error));
            });
    }

    private _createEndpointWithAuthCheck(
        pageContext: IVssPageContext,
        endpoint: ServiceEndpoint,
        onCreate: (endpoint: ServiceEndpoint) => void,
        resourceCreatedResponse: IResourceCreatedResponse,
        tenantId: string,
        accessTokens: { [key: string]: string }): void {
        this._processEndpointWithAuthCheck(
            pageContext,
            endpoint,
            tenantId,
            accessTokens,
            (endpoint) => this._createEndpoint(pageContext, endpoint, onCreate, resourceCreatedResponse),
            false);
    }

    private _validateEndpoint(pageContext: IVssPageContext, endpoint: ServiceEndpoint, onUpdate: (endpoint: ServiceEndpoint) => void, resourceCreatedResponse: IResourceCreatedResponse): void {
        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        environmentService.updateServiceEndpoint(endpoint, endpoint.id)
            .then((updatedEnpoint: ServiceEndpoint) => {
                return onUpdate(updatedEnpoint);
            }, (error: any) => {
                publishTelemetry(pageContext, FeatureNames.ValidateK8sResFailed, { endpointCreationFailed: "true", provider: endpoint.data.authorizationType });
                this._actions.updateCreationState.invoke("Failed");
                resourceCreatedResponse.onError(getErrorMessage(error));
            });
    }

    private _validateEndpointWithAuthCheck(
        pageContext: IVssPageContext,
        endpoint: ServiceEndpoint,
        onUpdate: (endpoint: ServiceEndpoint) => void,
        resourceCreatedResponse: IResourceCreatedResponse,
        tenantId: string | undefined,
        accessTokens: { [key: string]: string }): void {
        this._processEndpointWithAuthCheck(
            pageContext,
            endpoint,
            tenantId,
            accessTokens,
            (endpoint) => this._validateEndpoint(pageContext, endpoint, onUpdate, resourceCreatedResponse),
            false
        );
    }


    private _processEndpointWithAuthCheck(
        pageContext: IVssPageContext,
        endpoint: ServiceEndpoint,
        tenantId: string | undefined,
        accessTokens: { [key: string]: string },
        onCheckComplete: (endpoint: ServiceEndpoint) => void,
        updateFetchingMethod: boolean): void {
        if (tenantId && endpoint.authorization.parameters.azureTenantId && tenantId !== endpoint.authorization.parameters.azureTenantId) {
            const azureTenantId = endpoint.authorization.parameters.azureTenantId
            if (!accessTokens[azureTenantId]) {
                const aadAuthorizer = new AadAuthorizer(pageContext);
                const projectData: IProjectData | undefined = PageContextHelper.getProject(pageContext);
                aadAuthorizer.getAccessTokenKey(projectData, azureTenantId, tenantId, undefined, null).then((accessTokenKey: string) => {
                    this._accessTokenActions.getAccessToken.invoke({
                        accessTokenKey: accessTokenKey,
                        resourceTenantId: azureTenantId
                    });
                    setAuthInEnpointRequest(pageContext, endpoint, accessTokenKey, updateFetchingMethod);
                    onCheckComplete(endpoint);
                });
            }
            else {
                setAuthInEnpointRequest(pageContext, endpoint, accessTokens[azureTenantId], updateFetchingMethod);
                onCheckComplete(endpoint);
            }
        }
        else {
            onCheckComplete(endpoint);
        }
    }

    private _pollForEndpointCompletionWithTimeout(pageContext: IVssPageContext, endpointToQuery: ServiceEndpoint, onSuccess: (endpoint: ServiceEndpoint) => void, onFailure: (error: string) => void, timeinMs: number) {
        let continuePolling = true;
        setTimeout(() => continuePolling = false, timeinMs);
        this._pollForEndpointCompletion(pageContext, endpointToQuery, onSuccess, onFailure, () => continuePolling);
    }

    private _pollForEndpointCompletion(pageContext: IVssPageContext, endpointToQuery: ServiceEndpoint, onSuccess: (endpoint: ServiceEndpoint) => void, onFailure: (error: string) => void, continuePolling: () => boolean) {
        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        let operationStatus = { "state": "InProgress", "statusMessage": "" };
        // Try to get the endpoint and check its status
        environmentService.getServiceEndpoint(endpointToQuery.id).
            then(queriedEndpoint => {
                if (!queriedEndpoint) {
                    onFailure(Resources.ServiceConnectionNotFound);
                }

                // operation status to be used for failed case
                if (!!queriedEndpoint.operationStatus) {
                    operationStatus = queriedEndpoint.operationStatus;
                }

                if (queriedEndpoint.isReady) {
                    onSuccess(queriedEndpoint);
                }
                else if (operationStatus.state === "Failed") {
                    onFailure(operationStatus.statusMessage);
                }
                else if (!continuePolling()) {
                    onFailure(Resources.ValidationPollTimeoutMessage);
                }
                else {
                    setTimeout(() => this._pollForEndpointCompletion(pageContext, endpointToQuery, onSuccess, onFailure, continuePolling), 2 * 1000 /* 2 seconds */);
                }

            }, error => {
                setTimeout(() => this._pollForEndpointCompletion(pageContext, endpointToQuery, onSuccess, onFailure, continuePolling), 2 * 1000 /* 2 seconds */);
            })
    }

    private _addKubernetesResource(
        pageContext: IVssPageContext,
        name: string | undefined,
        namespaceName: string | undefined,
        cluster: string | undefined,
        serviceEndpointId: string | undefined,
        environmentId: number,
        resourceCreatedResponse: IResourceCreatedResponse,
        isRetrying: boolean = false
    ): void {

        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        environmentService.addKubernetesResource({
            name: name,
            namespace: namespaceName,
            clusterName: cluster,
            serviceEndpointId: serviceEndpointId
        } as KubernetesResourceCreateParametersExistingEndpoint, environmentId)
            .then((resource: EnvironmentResource) => {
                publishTelemetry(pageContext, FeatureNames.AddK8sResComplete);
                this._actions.addKubernetesResource.invoke(resource);
            }, (error: any) => {
                if (error.status == 409 && !isRetrying) {
                    // In case of name conflict, retry once
                    publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { k8sResourceNameConflict: "true" });
                    const modifiedName = (name + "-" + cluster).substring(0, 256);
                    this._addKubernetesResource(pageContext, modifiedName, namespaceName, cluster, serviceEndpointId, environmentId, resourceCreatedResponse, true);
                }
                else {
                    publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { k8sResourceCreationFailed: "true" });
                    resourceCreatedResponse.onError(getErrorMessage(error));
                    this._actions.updateCreationState.invoke("Failed");
                }
            });
    }

    //  private _addKubernetesResource2(
    //     pageContext: IVssPageContext,
    //     name: string | undefined,
    //     namespaceName: string | undefined,
    //     cluster: string | undefined,
    //     serviceEndpoint: ServiceEndpoint,
    //     environmentId: number,
    //     resourceCreatedResponse: IResourceCreatedResponse): void {

    //     // this method currently handles ServiceAccount flow only
    //     const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");

    //     environmentService.addKubernetesResource({
    //         name: name,
    //         namespace: namespaceName,
    //         clusterName: cluster,
    //         endpoint: serviceEndpoint,
    //         tags: []
    //     } as KubernetesResourceCreateParametersNewEndpoint, environmentId)
    //         .then((resource: EnvironmentResource) => {

    //             publishTelemetry(pageContext, FeatureNames.AddK8sResComplete);
    //             this._actions.addKubernetesResource.invoke(resource);

    //         }, (error: any) => {

    //             publishTelemetry(pageContext, FeatureNames.AddK8sResFailed, { k8sResourceCreationFailed: "true" });
    //             resourceCreatedResponse.onError(getErrorMessage(error));
    //             this._actions.updateCreationState.invoke("Failed");
    //         });
    // }

    private _getKubernetesServiceAccountEndpointObject(kubernetesCustomProviderData: IKubernetesCustomProviderData): ServiceEndpoint {
        let kubectlSecretJson = safeParseJson(kubernetesCustomProviderData.kubectlSecret);

        return {
            "data": {
                "authorizationType": "ServiceAccount",
                "acceptUntrustedCerts": kubernetesCustomProviderData.acceptUntrustedCertificates
            },
            "name": kubernetesCustomProviderData.serviceAccountName!.substring(0, 511),
            "type": "kubernetes",
            "url": kubernetesCustomProviderData.kubernetesServerUrl,
            "authorization": {
                "scheme": "Token",
                "parameters": {
                    "apiToken": kubectlSecretJson.data["token"], // this should be safe as we are already doing JSON validations in the UI
                    "serviceAccountCertificate": kubectlSecretJson.data["ca.crt"],
                    "isCreatedFromSecretYaml": false
                }
            },
        } as any as ServiceEndpoint;
    }

    private _getAzureKubernetesEndpointObject(azureKubernetesProviderData: IAzureKubernetesProviderData): ServiceEndpoint {
        const serviceEndpoint = {
            "data": {
                "authorizationType": "AzureSubscription",
                "azureSubscriptionId": azureKubernetesProviderData.subscription!.subscriptionId,
                "azureSubscriptionName": azureKubernetesProviderData.subscription!.displayName,
                "clusterId": azureKubernetesProviderData.cluster!.id,
                "namespace": azureKubernetesProviderData.kubernetesNamespace
            },
            "name": (azureKubernetesProviderData.environmentName + "-" + azureKubernetesProviderData.cluster!.name + "-" + azureKubernetesProviderData.kubernetesNamespace + "-" + new Date().getTime().toString()).substring(0, 511),
            "type": "kubernetes",
            "url": azureKubernetesProviderData.cluster!.fqdn,
            "authorization": {
                "scheme": "Kubernetes",
                "parameters": {
                    "azureEnvironment": "AzureCloud",
                    "azureTenantId": azureKubernetesProviderData.subscription!.subscriptionTenantId
                }
            },
        } as any as ServiceEndpoint;

        if (azureKubernetesProviderData.createNamespace) {
            serviceEndpoint.data["operation.createNamespace"] = "true";
        }

        return serviceEndpoint;
    }

    private _isEnvironmentOwnedServiceConnectionsFeatureFlagEnabled(pageContext: IVssPageContext): boolean {
        return isFeatureFlagEnabled(pageContext, "Environments.EnableEnvironmentOwnedServiceConnections", false)
    }
    
    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<KubernetesResourceActions>(KubernetesResourceActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _actions: KubernetesResourceActions;
    private _messageActions: MessagesActions;
    private _accessTokenActions: AccessTokenActions;
}
